<script setup lang="ts">
import { computed, ref } from "vue";
import { Check, ChevronDown, Trash2 } from "@lucide/vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";

// Types
import { UserInProjectType } from "@/features/BuildingProject";

const props = defineProps<{
  user: UserInProjectType;
  selfIsContributer: boolean;
  selfIsAdmin: boolean;
  isSelf: boolean;
  isPending?: boolean;
  /** Show the role without offering to change it — the same plain-text row a non-manager gets. */
  readOnly?: boolean;
}>();

const emits = defineEmits<{
  remove: [user: UserInProjectType];
  changeRole: [user: UserInProjectType, newRole: string];
}>();

// An Admin can manage anyone but themselves; a Contributor can manage anyone but an Admin or
// themselves. Unchanged from the original row menu — only the presentation has changed.
const canManage = computed(
  () => !props.readOnly && ((props.selfIsContributer && props.user.role !== "admin") || props.selfIsAdmin) && !props.isSelf
);

// A Contributor may only assign Contributor or Viewer, never Admin — the original menu expressed
// this by never rendering a "Move to Admin" item.
const assignableRoles = computed(() => (props.selfIsAdmin ? ["admin", "contributor", "viewer"] : ["contributor", "viewer"]));

const roleLabels: Record<string, string> = { admin: "Admin", contributor: "Contributor", viewer: "Viewer" };
const roleLabel = computed(() => roleLabels[props.user.role] ?? props.user.role);

const menuItemClass =
  "focus-ring rounded-4 t-small flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-100";

// Removal is immediate and has no undo, so the menu asks first rather than acting on the click.
// Confirming inline (instead of opening a second modal on top of this one) keeps it out of the
// nested-modal stacking that Modal.vue's own z-index management would otherwise have to handle.
const isConfirmingRemove = ref(false);

const handleChangeRole = (newRole: string, close: () => void) => {
  close();
  // Picking the role someone already has is a no-op rather than a pointless round trip.
  if (newRole === props.user.role) return;
  emits("changeRole", props.user, newRole);
};

const handleRemove = (close: () => void) => {
  close();
  emits("remove", props.user);
};
</script>

<template>
  <!-- The role reads as plain text in the same spot on every row. Where it can be changed it's a
       quiet ghost button rather than a bordered <select>: a form control per row made the list
       look ragged and read as a form instead of a member list. The label comes from `user.role`,
       which the parent only updates once the request has actually succeeded, so it can never
       show a role the server hasn't confirmed. -->
  <Popover
    v-if="canManage"
    placement="bottom-end"
    role="menu"
    :teleport="true"
    panel-z="z-tooltip"
    panel-class="w-60 p-1"
    aria-label="Change role or remove"
    @close="isConfirmingRemove = false">
    <template #trigger="{ toggle, isOpen }">
      <button
        type="button"
        :disabled="isPending"
        :aria-expanded="isOpen"
        aria-haspopup="menu"
        :aria-label="`Change role or remove ${user.name}`"
        class="focus-ring interactive-active rounded-4 duration-fast ease-standard t-small text-type-default gap-px-4 flex shrink-0 cursor-pointer items-center px-2 py-1 transition-colors hover:bg-slate-100"
        :class="{ 'interactive-disabled': isPending }"
        @click="toggle">
        {{ roleLabel }}
        <!-- Role changes take a moment against the API and the label deliberately doesn't move
             until the server confirms, so without this the row would sit there looking inert. -->
        <LoadingSpinner
          v-if="isPending"
          size="xs"
          fill="gray"
          label="Updating" />
        <ChevronDown
          v-else
          class="icon-xs text-type-muted"
          :stroke-width="1.5" />
      </button>
    </template>

    <template #default="{ close }">
      <template v-if="!isConfirmingRemove">
        <p class="t-footnote text-type-muted px-3 pt-1 pb-1 tracking-wide uppercase">Role</p>
        <button
          v-for="role in assignableRoles"
          :key="role"
          type="button"
          role="menuitem"
          :class="menuItemClass"
          @click="handleChangeRole(role, close)">
          <Check
            v-if="role === user.role"
            class="icon-xs text-app-blue-600 shrink-0"
            :stroke-width="2" />
          <!-- Keeps labels aligned whether or not a row carries the tick. -->
          <span
            v-else
            class="icon-xs shrink-0" />
          {{ roleLabels[role] }}
        </button>

        <div class="mt-1 border-t border-slate-200 pt-1">
          <button
            type="button"
            role="menuitem"
            :class="[menuItemClass, 'text-app-red-600']"
            @click="isConfirmingRemove = true">
            <Trash2
              class="icon-xs shrink-0"
              :stroke-width="1.5" />
            Remove from project
          </button>
        </div>
      </template>

      <div
        v-else
        class="p-2">
        <p class="t-small text-type-default">
          Remove <span class="font-semibold">{{ user.name }}</span> from this project?
        </p>
        <p class="t-caption text-type-muted mt-1">They lose access immediately. You can invite them again later.</p>
        <div class="mt-3 flex justify-end gap-2">
          <button
            type="button"
            class="focus-ring interactive-active rounded-4 t-small text-type-muted hover:text-type-default duration-fast ease-standard cursor-pointer px-3 py-1.5 transition-colors hover:bg-slate-100"
            @click="isConfirmingRemove = false">
            Cancel
          </button>
          <button
            type="button"
            class="focus-ring interactive-active rounded-4 t-small-bold bg-app-red-600 dark:bg-app-red-fill hover:bg-app-red-700 dark:hover:bg-app-red-fill-hover duration-fast ease-standard cursor-pointer px-3 py-1.5 text-white transition-colors"
            @click="handleRemove(close)">
            Remove
          </button>
        </div>
      </div>
    </template>
  </Popover>

  <div
    v-else
    class="t-small text-type-muted shrink-0 px-2 py-1">
    {{ roleLabel }}
  </div>
</template>
